import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFarcaster } from '../../contexts/FarcasterContext';
import { useNFT } from '../../contexts/NFTContext';
import { useFarcasterMiniApp } from '../../hooks/useFarcasterMiniApp';
import FarcasterQuickAuth, { useFarcasterQuickAuth } from '../FarcasterQuickAuth';

/**
 * CRITICAL: Main Navigation Component
 * 
 * This component provides:
 * - Site navigation and branding
 * - Seamless user authentication display
 * - Mobile responsive menu
 * - Active route highlighting
 * 
 * Automatically detects Farcaster Mini App context for seamless UX.
 * Shows username directly when in Farcaster environment.
 */

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isMiniApp, user: miniAppUser } = useFarcasterMiniApp();
  
  // Use Farcaster Quick Auth as fallback for web environments
  const { user: quickAuthUser, isAuthenticated: isQuickAuthAuthenticated, logout: quickAuthLogout } = useFarcasterQuickAuth();
  
  // Determine the active user and authentication state
  // Priority: Mini App user > Quick Auth user
  const currentUser = miniAppUser || quickAuthUser;
  const isAuthenticated = !!currentUser;
  const isFromMiniApp = !!miniAppUser;

  const handleLogout = () => {
    if (isFromMiniApp) {
      // For Mini App users, just clear the local data (they're still in Farcaster)
      localStorage.removeItem('farcaster_miniapp_user');
      window.location.reload(); // Refresh to re-detect context
    } else {
      // For Quick Auth users, use the proper logout
      quickAuthLogout();
    }
  };

  const navigationItems = [
    { name: 'Explore', href: '/explore', icon: '🔍' },
    { name: 'Profile', href: '/profile', icon: '👤' },
  ];

  const isActivePath = (path) => {
    return location.pathname === path || 
           (path === '/profile' && location.pathname.startsWith('/profile'));
  };

  return (
    <nav className="bg-secondary-800 border-b border-secondary-700 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and brand */}
          <div className="flex items-center">
            <Link 
              to="/explore" 
              className="flex items-center space-x-3 text-white hover:text-gold-400 transition-colors"
            >
              <div className="w-8 h-8 bg-castoria-gradient rounded-lg flex items-center justify-center font-bold text-white shadow-glow">
                🏰
              </div>
              <span className="font-bold text-xl hidden sm:block">
                Castoria
              </span>
            </Link>
          </div>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${isActivePath(item.href)
                    ? 'bg-castoria-gradient text-white shadow-glow'
                    : 'text-secondary-300 hover:text-white hover:bg-secondary-700'
                  }
                `}
              >
                <span className="text-lg">{item.icon}</span>
                <span>{item.name}</span>
              </Link>
            ))}
          </div>

          {/* User status */}
          <div className="flex items-center space-x-4">
            {/* Authentication - Desktop */}
            <div className="hidden sm:flex items-center space-x-3">
              {isAuthenticated ? (
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-2 text-sm text-secondary-300">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span>@{currentUser.username}</span>
                    {isMiniApp && (
                      <span className="px-2 py-1 text-xs bg-castoria-gradient text-white rounded-full shadow-glow">
                        ⚡ Fortress
                      </span>
                    )}
                  </div>
                  {!isFromMiniApp && (
                    <button
                      onClick={handleLogout}
                      className="text-xs text-secondary-400 hover:text-white transition-colors"
                    >
                      Disconnect
                    </button>
                  )}
                </div>
              ) : (
                <FarcasterQuickAuth 
                  className="px-4 py-2 bg-farcaster-600 hover:bg-farcaster-500 text-white rounded-lg font-medium transition-colors"
                  buttonText="🎯 Sign in with Farcaster"
                />
              )}
            </div>

            {/* Authentication - Mobile */}
            <div className="sm:hidden flex items-center">
              {isAuthenticated ? (
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1 text-xs text-secondary-300">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span>@{currentUser.username}</span>
                  </div>
                </div>
              ) : (
                <FarcasterQuickAuth 
                  className="px-3 py-1 bg-farcaster-600 hover:bg-farcaster-500 text-white rounded-md text-sm font-medium transition-colors"
                  buttonText="🎯 Sign in"
                />
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-secondary-400 hover:text-white hover:bg-secondary-700 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 border-t border-secondary-700">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`
                    flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-colors
                    ${isActivePath(item.href)
                      ? 'bg-castoria-gradient text-white shadow-glow'
                      : 'text-secondary-300 hover:text-white hover:bg-secondary-700'
                    }
                  `}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span>{item.name}</span>
                </Link>
              ))}

              {/* Mobile Authentication */}
              <div className="border-t border-secondary-700 pt-4">
                {isAuthenticated ? (
                  <div className="px-3 py-4 space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-secondary-300">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span>Connected as @{currentUser.username}</span>
                      {isMiniApp && (
                        <span className="px-2 py-1 text-xs bg-castoria-gradient text-white rounded-full shadow-glow">
                          ⚡ Fortress
                        </span>
                      )}
                    </div>
                    {!isFromMiniApp && (
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMobileMenuOpen(false);
                        }}
                        className="text-sm text-secondary-400 hover:text-white transition-colors"
                      >
                        Disconnect Farcaster
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="px-3 py-4 space-y-3">
                    <div className="text-sm font-medium text-white">
                      🎭 Sign in with Farcaster to access all fortress features
                    </div>
                    <FarcasterQuickAuth 
                      className="w-full px-4 py-2 bg-farcaster-600 hover:bg-farcaster-500 text-white rounded-lg font-medium transition-colors"
                      buttonText="🎯 Sign in with Farcaster"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 